import {Container, Grid} from '@mui/material'
import type {Breakpoint} from '@mui/system'
import {memo, useMemo} from 'react'
import type {Iri} from '../../../@types/Api'
import type {SectorPartial} from '../../../@types/entity/Sector'
import type {VisitPartial} from '../../../@types/entity/Visit'
import type {PropsWithError} from '../../../components/Skeleton/Skeleton'
import {useSectorCollection} from '../../../hooks/entity/useSector'
import {useSpotCollection} from '../../../hooks/entity/useSpot'
import {useVisitCollection, useVisitFilterFromCurrentUser} from '../../../hooks/entity/useVisit'
import {range} from '../../../utils/array'
import {SpotCard, SpotCardSkeleton} from './SpotCard'

interface Props {
  spotIris: Iri[]
  linkPattern: string
  limit?: number
}

export const SpotList = memo(({spotIris, linkPattern, limit}: Props) => {
  const {spots, error: spotError} = useSpotCollection({spotIris})
  const {sectors, error: sectorError} = useSectorCollection({spotIris})
  const {visits, error: visitError} = useVisitCollection({...useVisitFilterFromCurrentUser(), stared: true})

  const visitsBySpot = useMemo(() => {
    const map: Map<Iri, VisitPartial> = new Map()
    if (!visits) {
      return map
    }

    for (const visit of visits.member) {
      map.set(visit.spot, visit)
    }

    return map
  }, [visits])

  const sectorsBySpot = useMemo(() => {
    const map: Map<Iri, SectorPartial[]> = new Map()
    if (!sectors) {
      return map
    }

    for (const sector of sectors.member) {
      const sectors = map.get(sector.spot)
      if (!sectors) {
        map.set(sector.spot, [sector])
      } else {
        sectors.push(sector)
      }
    }

    return map
  }, [sectors])

  if (spotIris.length === 0) {
    return null
  }

  if (!spots || !sectors || !visits) {
    return <SpotListSkeleton
      error={spotError ?? sectorError ?? visitError}
      count={visits ? Math.min(limit ?? 3, visits.member.length) : 3}
    />
  }

  const items = spots.member.sort((a, b) => {
    const va = visitsBySpot.get(a['@id'])
    const vb = visitsBySpot.get(b['@id'])
    if (va) {
      if (!vb) {
        return -1
      }
      if (va.stared && !vb.stared) {
        return -1
      }
      if (vb.stared && !va.stared) {
        return 1
      }
      return vb.aggregate.approximateLastVisitAt.localeCompare(va.aggregate.approximateLastVisitAt)
    }
    if (vb) {
      return 1
    }
    return a.name.localeCompare(b.name)
  }).slice(0, limit)
  const mdRowCount = 2
  const mdOrphanCount = items.length % mdRowCount
  const xlRowCount = [2, 4].includes(items.length) ? 2 : 3
  const xlOrphanCount = items.length % xlRowCount

  let containerSize: Breakpoint = 'xl'
  if (items.length === 1) {
    containerSize = 'xs'
  } else if ([2, 4].includes(items.length)) {
    containerSize = 'md'
  }

  return <Container disableGutters maxWidth={containerSize}>
    <Grid container spacing={2} sx={{alignItems: 'stretch'}}>
      {items.map((spot, index) => <Grid
        key={spot['@id']}
        size={{
          xs: 12,
          md: index < mdOrphanCount ? 12 / mdOrphanCount : 12 / mdRowCount,
          xl: index < xlOrphanCount ? 12 / xlOrphanCount : 12 / xlRowCount,
        }}
      >
        <SpotCard
          visit={visitsBySpot.get(spot['@id'])}
          spot={spot}
          sectors={sectorsBySpot.get(spot['@id']) ?? []}
          link={linkPattern.replace(/\{spotSlug\}/g, spot.slug)}
        />
      </Grid>)}
    </Grid>
  </Container>
})

const SpotListSkeleton = memo(({count = 3, error}: {
  count?: number
} & PropsWithError) => {
  const mdRowCount = 2
  const mdOrphanCount = count % mdRowCount
  const xlRowCount = [2, 4].includes(count) ? 2 : 3
  const xlOrphanCount = count % xlRowCount

  let containerSize: Breakpoint = 'xl'
  if (count === 1) {
    containerSize = 'xs'
  } else if ([2, 4].includes(count)) {
    containerSize = 'md'
  }

  return <Container disableGutters maxWidth={containerSize}>
    <Grid container spacing={2} sx={{alignItems: 'stretch'}}>
      {range(0, count - 1).map((index) => <Grid
        key={index}
        size={{
          xs: 12,
          md: index < mdOrphanCount ? 12 / mdOrphanCount : 12 / mdRowCount,
          xl: index < xlOrphanCount ? 12 / xlOrphanCount : 12 / xlRowCount,
        }}
      >
        <SpotCardSkeleton error={error}/>
      </Grid>)}
    </Grid>
  </Container>
})
